/**
 * ValueEnum / Select Options 转换工具
 *
 * 用于将常量映射表转换为 ProTable valueEnum 格式或 Ant Design Select options 格式。
 * 三个项目大量使用 ProTable 的 valueEnum，均有手工转换逻辑。
 *
 */

/**
 * ProTable valueEnum 项
 */
export interface ValueEnumItem {
  /** 显示文本 */
  text: string;
  /** 状态（ProTable 内置：success, error, processing, warning, default） */
  status?: 'success' | 'error' | 'processing' | 'warning' | 'default';
  /** 颜色 */
  color?: string;
  /** 是否禁用 */
  disabled?: boolean;
}

/**
 * ProTable valueEnum 类型
 */
export type ValueEnumMap = Record<string | number, ValueEnumItem | string>;

/**
 * Select/Radio 选项
 */
export interface OptionItem<V = string | number> {
  /** 显示文本 */
  label: string;
  /** 值 */
  value: V;
  /** 颜色（可选） */
  color?: string;
  /** 是否禁用 */
  disabled?: boolean;
}

/**
 * 常量映射项的通用结构
 */
interface ConstantMapItem {
  text: string;
  color?: string;
  status?: 'success' | 'error' | 'processing' | 'warning' | 'default';
  disabled?: boolean;
}

/**
 * createValueEnum 选项
 */
export interface CreateValueEnumOptions {
  /** 当 useOriginalStatus 为 false 时使用的默认 status */
  defaultStatus?: 'success' | 'error' | 'processing' | 'warning' | 'default';
  /** 是否使用原始映射中的 status 字段，默认 true */
  useOriginalStatus?: boolean;
}

/**
 * 将常量映射表转换为 ProTable valueEnum 格式
 *
 * @param map - 常量映射表 `{ key: { text, color?, status? } }`
 * @param options - 可选配置
 * @returns ProTable 可用的 valueEnum 对象
 *
 * @example
 * ```ts
 * const STATUS_MAP = {
 *   1: { text: '启用', color: 'green', status: 'success' },
 *   0: { text: '禁用', color: 'red', status: 'error' },
 * } as const;
 *
 * // 使用原始 status
 * const valueEnum = createValueEnum(STATUS_MAP);
 *
 * // 统一使用 Default status
 * const valueEnum2 = createValueEnum(STATUS_MAP, {
 *   useOriginalStatus: false,
 *   defaultStatus: 'default',
 * });
 *
 * <ProTable columns={[{ title: '状态', dataIndex: 'status', valueEnum }]} />
 * ```
 */
export function createValueEnum(
  map: Record<string | number, ConstantMapItem>,
  options?: CreateValueEnumOptions,
): ValueEnumMap {
  const { defaultStatus = 'default', useOriginalStatus = true } = options ?? {};
  const result: ValueEnumMap = {};

  for (const [key, item] of Object.entries(map)) {
    const status =
      useOriginalStatus && item.status ? item.status : defaultStatus;

    result[key] = {
      text: item.text,
      status,
      ...(item.color ? { color: item.color } : {}),
      ...(item.disabled ? { disabled: item.disabled } : {}),
    };
  }

  return result;
}

/**
 * 从数组创建 valueEnum
 *
 * @param arr - 数据数组
 * @param valueKey - 值字段名
 * @param labelKey - 文本字段名
 * @param options - 可选配置
 * @returns ProTable 可用的 valueEnum 对象
 *
 * @example
 * ```ts
 * const categories = [
 *   { id: 1, name: '分类A' },
 *   { id: 2, name: '分类B' },
 * ];
 * const valueEnum = createValueEnumFromArray(categories, 'id', 'name');
 * // => { 1: { text: '分类A' }, 2: { text: '分类B' } }
 * ```
 */
export function createValueEnumFromArray<T extends Record<string, unknown>>(
  arr: T[],
  valueKey: keyof T & string,
  labelKey: keyof T & string,
  options?: {
    /** 颜色字段名 */
    colorKey?: keyof T & string;
    /** 状态字段名 */
    statusKey?: keyof T & string;
  },
): ValueEnumMap {
  const result: ValueEnumMap = {};

  for (const item of arr) {
    const key = String(item[valueKey]);
    const text = String(item[labelKey]);
    const enumItem: ValueEnumItem = { text };

    if (options?.colorKey && item[options.colorKey]) {
      enumItem.color = String(item[options.colorKey]);
    }
    if (options?.statusKey && item[options.statusKey]) {
      enumItem.status = item[options.statusKey] as ValueEnumItem['status'];
    }

    result[key] = enumItem;
  }

  return result;
}

/**
 * 将常量映射表转换为 Select/Radio 的 options 数组
 *
 * @param map - 常量映射表
 * @returns Select/Radio 可用的 options 数组
 *
 * @example
 * ```ts
 * const STATUS_MAP = {
 *   1: { text: '启用', color: 'green' },
 *   0: { text: '禁用', color: 'red' },
 * };
 *
 * const options = createSelectOptionsFromMap(STATUS_MAP);
 * // => [{ label: '启用', value: '1', color: 'green' }, { label: '禁用', value: '0', color: 'red' }]
 *
 * <Select options={options} />
 * ```
 */
export function createSelectOptionsFromMap(
  map: Record<string | number, ConstantMapItem>,
): OptionItem<string>[] {
  return Object.entries(map).map(([key, item]) => ({
    label: item.text,
    value: key,
    ...(item.color ? { color: item.color } : {}),
    ...(item.disabled ? { disabled: item.disabled } : {}),
  }));
}

/**
 * 从数组创建 Select/Radio 的 options 数组
 *
 * @param arr - 数据数组
 * @param valueKey - 值字段名
 * @param labelKey - 文本字段名
 * @returns options 数组
 *
 * @example
 * ```ts
 * const roles = [{ id: 1, roleName: '管理员' }, { id: 2, roleName: '普通用户' }];
 * const options = createSelectOptionsFromArray(roles, 'id', 'roleName');
 * // => [{ label: '管理员', value: 1 }, { label: '普通用户', value: 2 }]
 * ```
 */
export function createSelectOptionsFromArray<
  T extends Record<string, unknown>,
  V = T[keyof T],
>(
  arr: T[],
  valueKey: keyof T & string,
  labelKey: keyof T & string,
): OptionItem<V>[] {
  return arr.map((item) => ({
    label: String(item[labelKey]),
    value: item[valueKey] as V,
  }));
}

/**
 * 获取枚举映射中指定值的显示文本
 *
 * @param map - 常量映射表
 * @param value - 枚举值
 * @param defaultText - 默认文本，默认 '--'
 * @returns 显示文本
 *
 * @example
 * ```ts
 * getEnumText(STATUS_MAP, 1) // '启用'
 * getEnumText(STATUS_MAP, 999) // '--'
 * ```
 */
export function getEnumText(
  map: Record<string | number, ConstantMapItem>,
  value: string | number | null | undefined,
  defaultText: string = '--',
): string {
  if (value === null || value === undefined) return defaultText;
  return map[value]?.text ?? defaultText;
}

/**
 * 获取枚举映射中指定值的颜色
 *
 * @param map - 常量映射表
 * @param value - 枚举值
 * @param defaultColor - 默认颜色，默认 'default'
 * @returns 颜色值
 *
 * @example
 * ```ts
 * getEnumColor(STATUS_MAP, 1) // 'green'
 * getEnumColor(STATUS_MAP, 999) // 'default'
 * ```
 */
export function getEnumColor(
  map: Record<string | number, ConstantMapItem>,
  value: string | number | null | undefined,
  defaultColor: string = 'default',
): string {
  if (value === null || value === undefined) return defaultColor;
  return map[value]?.color ?? defaultColor;
}
